import { useContext } from 'react';
import { ContextAuth } from '../../../contexts/Authentication';
import { useCreateAudit } from './useAudit';
import {
  AuditAction,
  AuditCategory,
  AuditLevel,
  AuditResourceType,
  ICreateAuditData,
} from './types';

/**
 * Builder Pattern para construção de logs de auditoria
 * Permite criar logs de forma fluente e intuitiva
 */
export class AuditBuilder {
  private data: Partial<ICreateAuditData>;

  constructor(userId: string, role: 'candidate' | 'admin' = 'candidate') {
    this.data = {
      user_id: userId,
      role: role,
      level: 'info',
      timestamp: new Date().toISOString(),
    };
  }

  action(action: AuditAction): this {
    this.data.action = action;
    return this;
  }

  level(level: AuditLevel): this {
    this.data.level = level;
    return this;
  }

  category(category: AuditCategory): this {
    this.data.category = category;
    return this;
  }

  resourceType(resourceType: AuditResourceType): this {
    this.data.resource_type = resourceType;
    return this;
  }

  resourceId(resourceId: string): this {
    this.data.resource_id = resourceId;
    return this;
  }

  selectiveProcessId(selectiveProcessId: string): this {
    this.data.selective_process_id = selectiveProcessId;
    return this;
  }

  stepId(stepId: string): this {
    this.data.step_id = stepId;
    return this;
  }

  message(message: string): this {
    this.data.message = message;
    return this;
  }

  build(): ICreateAuditData {
    if (!this.data.action) {
      throw new Error('Action is required for audit log');
    }
    return this.data as ICreateAuditData;
  }
}

/**
 * Hook principal para registro de logs de auditoria
 * Fornece métodos práticos para registrar diferentes tipos de eventos
 */
export const useAuditLogger = () => {
  const { candidate } = useContext(ContextAuth);
  const createAudit = useCreateAudit();

  /**
   * Cria um builder pré-configurado com dados do usuário atual
   */
  const createBuilder = (): AuditBuilder => {
    return new AuditBuilder(candidate?.user?.id || 'anonymous', 'candidate');
  };

  /**
   * Registra um log usando o builder pattern
   */
  const logWithBuilder = async (builderFn: (builder: AuditBuilder) => AuditBuilder) => {
    try {
      const builder = createBuilder();
      const data = builderFn(builder).build();
      await createAudit.mutateAsync(data);
    } catch (error) {
      console.error('Failed to log audit:', error);
    }
  };

  /**
   * Registra um log de autenticação
   */
  const logAuthAction = async (
    action: AuditAction,
    message: string,
    level: AuditLevel = 'info'
  ) => {
    try {
      await createAudit.mutateAsync({
        user_id: candidate?.user?.id || 'anonymous',
        role: 'candidate',
        action,
        level,
        category: 'authentication',
        resource_type: 'user_session',
        message,
      });
    } catch (error) {
      console.error('Failed to log auth action:', error);
    }
  };

  /**
   * Registra uma ação no perfil do candidato
   */
  const logProfileAction = async (
    action: AuditAction,
    message: string,
    level: AuditLevel = 'info',
    changes?: Record<string, { old: unknown; new: unknown }>
  ) => {
    try {
      let finalMessage = message;
      
      // Se houver mudanças, adiciona ao log
      if (changes) {
        const changeDetails = Object.entries(changes)
          .map(([field, { old, new: newValue }]) => {
            return `${field}: "${old}" → "${newValue}"`;
          })
          .join(', ');
        finalMessage = `${message}. Alterações: ${changeDetails}`;
      }

      await createAudit.mutateAsync({
        user_id: candidate?.user?.id || 'anonymous',
        role: 'candidate',
        action,
        level,
        category: 'profile_management',
        resource_type: 'user_profile',
        resource_id: candidate?.id,
        message: finalMessage,
      });
    } catch (error) {
      console.error('Failed to log profile action:', error);
    }
  };

  /**
   * Registra uma ação de inscrição
   */
  const logRegistrationAction = async (
    action: AuditAction,
    selectiveProcessId: string,
    message: string,
    level: AuditLevel = 'info',
    registrationId?: string
  ) => {
    try {
      await createAudit.mutateAsync({
        user_id: candidate?.user?.id || 'anonymous',
        role: 'candidate',
        action,
        level,
        category: 'registration',
        resource_type: 'registration',
        resource_id: registrationId,
        selective_process_id: selectiveProcessId,
        message,
      });
    } catch (error) {
      console.error('Failed to log registration action:', error);
    }
  };

  /**
   * Registra uma ação de documento
   */
  const logDocumentAction = async (
    action: AuditAction,
    documentId: string,
    documentName: string,
    level: AuditLevel = 'info',
    selectiveProcessId?: string
  ) => {
    try {
      await createAudit.mutateAsync({
        user_id: candidate?.user?.id || 'anonymous',
        role: 'candidate',
        action,
        level,
        category: 'document_management',
        resource_type: 'document_upload',
        resource_id: documentId,
        selective_process_id: selectiveProcessId,
        message: `${action} - ${documentName}`,
      });
    } catch (error) {
      console.error('Failed to log document action:', error);
    }
  };

  /**
   * Registra progresso em etapas
   */
  const logStepAction = async (
    action: AuditAction,
    stepId: string,
    stepName: string,
    selectiveProcessId: string,
    level: AuditLevel = 'info',
    submissionId?: string
  ) => {
    try {
      await createAudit.mutateAsync({
        user_id: candidate?.user?.id || 'anonymous',
        role: 'candidate',
        action,
        level,
        category: 'step_progress',
        resource_type: 'step_submission',
        resource_id: submissionId,
        selective_process_id: selectiveProcessId,
        step_id: stepId,
        message: `${stepName} - ${action}`,
      });
    } catch (error) {
      console.error('Failed to log step action:', error);
    }
  };

  /**
   * Registra acesso a páginas/seções
   */
  const logPageAccess = async (
    pageName: string,
    pageUrl: string,
    selectiveProcessId?: string
  ) => {
    try {
      await createAudit.mutateAsync({
        user_id: candidate?.user?.id || 'anonymous',
        role: 'candidate',
        action: 'page_accessed',
        level: 'info',
        category: 'authentication',
        resource_type: 'user_session',
        selective_process_id: selectiveProcessId,
        message: `Acessou página: ${pageName} (${pageUrl})`,
      });
    } catch (error) {
      console.error('Failed to log page access:', error);
    }
  };

  /**
   * Registra exportação de dados
   */
  const logDataExport = async (
    exportType: string,
    message: string,
    selectiveProcessId?: string
  ) => {
    try {
      await createAudit.mutateAsync({
        user_id: candidate?.user?.id || 'anonymous',
        role: 'candidate',
        action: 'data_exported',
        level: 'info',
        category: 'data_export',
        resource_type: 'data_export',
        selective_process_id: selectiveProcessId,
        message: `Exportou: ${exportType} - ${message}`,
      });
    } catch (error) {
      console.error('Failed to log data export:', error);
    }
  };

  /**
   * Registra um log genérico com todos os parâmetros
   */
  const log = async (data: Omit<ICreateAuditData, 'user_id' | 'role'>) => {
    try {
      await createAudit.mutateAsync({
        ...data,
        user_id: candidate?.user?.id || 'anonymous',
        role: 'candidate',
      });
    } catch (error) {
      console.error('Failed to log audit:', error);
    }
  };

  return {
    // Métodos principais
    log,
    logWithBuilder,
    createBuilder,
    
    // Métodos específicos por categoria
    logAuthAction,
    logProfileAction,
    logRegistrationAction,
    logDocumentAction,
    logStepAction,
    logPageAccess,
    logDataExport,
    
    // Estado do mutation
    isLogging: createAudit.isPending,
    error: createAudit.error,
  };
};
